import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  HiX,
  HiOutlineDocumentDownload,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineArrowLeft,
  HiOutlineSearch,
} from 'react-icons/hi';
import { patientsApi, docxTemplatesApi, invoicesApi, assessmentsApi } from '../../services/api';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';

// Per-data-shape display config: which period picker, which columns to show,
// and a default human-readable description. The backend resolves template_code
// -> data_shape, so admin-defined codes that reuse a known shape behave the same.
const SHAPE_CONFIG = {
  ongc_weekly: {
    label: 'ONGC Weekly Bill',
    blurb: 'One row per therapy with the list of present dates that week.',
    period: 'week',
    columns: ['plan_name', 'present_dates', 'per_session_charge', 'no_of_sessions', 'total'],
  },
  ongc_session: {
    label: 'ONGC Session Distribution',
    blurb: 'Monthly bill: one row per therapy with sessions count and charge.',
    period: 'month',
    columns: ['plan_name', 'month', 'per_session_charge', 'no_of_sessions', 'total'],
  },
  ongc_general: {
    label: 'ONGC General Receipt',
    blurb: 'Compact monthly receipt with one combined therapy line and total.',
    period: 'month',
    columns: ['plan_name', 'month', 'total'],
  },
  prl_bill: {
    label: 'PRL Bill Format',
    blurb: 'Date-range invoice for Physical Research Laboratory billing.',
    period: 'range',
    columns: ['plan_name', 'month_range', 'per_session_charge', 'no_of_sessions', 'total'],
  },
  attendance_bifurcation: {
    label: 'Attendance Bifurcation',
    blurb: 'Day-by-day attendance grid for the chosen month.',
    defaultSheetTitle: 'Attendance Sheet for OT and ABA Sessions',
    period: 'month',
    columns: ['days_grid'],
  },
};

// Last-resort fallback if the API list call fails — guarantees exports keep
// working even if the templates table or S3 is unreachable.
const FALLBACK_OPTIONS = Object.keys(SHAPE_CONFIG).map((shape) => ({
  template_code: shape,
  data_shape: shape,
  name: SHAPE_CONFIG[shape].label,
  description: SHAPE_CONFIG[shape].blurb,
  is_system: true,
}));

function ymd(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function startOfMonth(date) { return new Date(date.getFullYear(), date.getMonth(), 1); }
function endOfMonth(date) { return new Date(date.getFullYear(), date.getMonth() + 1, 0); }
function startOfWeek(date) {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  const diffToMon = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  d.setDate(d.getDate() + diffToMon);
  return d;
}
function endOfWeek(date) {
  const s = startOfWeek(date);
  s.setDate(s.getDate() + 6);
  return s;
}

function defaultPeriod(periodKind) {
  const today = new Date();
  if (periodKind === 'week') {
    return { period_from: ymd(startOfWeek(today)), period_to: ymd(endOfWeek(today)) };
  }
  if (periodKind === 'range') {
    return { period_from: ymd(startOfMonth(today)), period_to: ymd(endOfMonth(today)) };
  }
  return { period_from: ymd(startOfMonth(today)), period_to: ymd(endOfMonth(today)) };
}

const PAYMENT_MODES = [
  { value: 'online', label: 'Online' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
];

function formatAssessmentLabel(assessment) {
  if (!assessment) return '—';
  const date = formatCalendarDateDMY(assessment.assessment_date);
  const parts = [];
  if (assessment.patient_name) parts.push(`Patient: ${assessment.patient_name}`);
  if (assessment.lead_name) parts.push(`Lead: ${assessment.lead_name}`);
  const namePart = parts.length > 0 ? parts.join(' / ') : 'Assessment';
  return `${date} — ${namePart}`;
}

export default function ExportInvoiceModal({
  patient,
  onClose,
  onExported,
  allowedShapes,
  step1Title = 'Generate Report / Invoice',
  includeDefaultInvoiceOption = false,
  includeAssessmentInvoiceOption = false,
  onChooseDefaultInvoice,
  onChooseAssessmentInvoice,
  onCreatedInvoiceId,
  editingInvoiceId,
  initialTemplateCode,
  initialExportMetadata,
}) {
  const isEditing = !!editingInvoiceId;
  const editPrefillAppliedRef = useRef(null);
  const showEntityChoice = !patient && includeAssessmentInvoiceOption && !editingInvoiceId;
  const [entityMode, setEntityMode] = useState('patient');
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [assessmentSearchQuery, setAssessmentSearchQuery] = useState('');
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const [step, setStep] = useState(patient ? 1 : 0); // 0: pick patient, 1: pick template, 2: form
  const allowedShapesSet = useMemo(() => {
    return Array.isArray(allowedShapes) && allowedShapes.length > 0 ? new Set(allowedShapes) : null;
  }, [allowedShapes]);
  const fallbackOptions = useMemo(() => {
    if (!allowedShapesSet) return FALLBACK_OPTIONS;
    return FALLBACK_OPTIONS.filter((row) => allowedShapesSet.has(row.data_shape));
  }, [allowedShapesSet]);

  const [templates, setTemplates] = useState(fallbackOptions);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedCode, setSelectedCode] = useState(null);
  const [period, setPeriod] = useState({ period_from: '', period_to: '' });
  const [manualWeekRange, setManualWeekRange] = useState(false);
  const [paymentMode, setPaymentMode] = useState('online');
  const [paymentDate, setPaymentDate] = useState(ymd(new Date()));
  const [notes, setNotes] = useState('');
  const [sheetTitle, setSheetTitle] = useState('');

  const [prefill, setPrefill] = useState(null);
  const [items, setItems] = useState([]);
  const [days, setDays] = useState([]);
  const [receiptNo, setReceiptNo] = useState('');
  const [loadingPrefill, setLoadingPrefill] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showTemplateStyle, setShowTemplateStyle] = useState(false);

  const selectedTemplate = templates.find((t) => t.template_code === selectedCode) || null;
  const shape = selectedTemplate?.data_shape || null;
  const shapeCfg = shape ? SHAPE_CONFIG[shape] : null;

  // Combined "tpl" object used by the existing render helpers below.
  const tpl = shapeCfg
    ? { code: selectedTemplate.template_code, data_shape: shape, ...shapeCfg }
    : null;

  const filteredPatients = useMemo(() => {
    const query = patientSearchQuery.trim().toLowerCase();
    if (!query) return patients;
    return patients.filter((p) => {
      const name = (p.full_name || '').toLowerCase();
      const code = (p.patient_code || '').toLowerCase();
      const phone = (p.contact_primary || '').toLowerCase();
      return name.includes(query) || code.includes(query) || phone.includes(query);
    });
  }, [patients, patientSearchQuery]);

  const filteredAssessments = useMemo(() => {
    const query = assessmentSearchQuery.trim().toLowerCase();
    if (!query) return assessments;
    return assessments.filter((a) => {
      const patientName = (a.patient_name || '').toLowerCase();
      const leadName = (a.lead_name || '').toLowerCase();
      const date = (a.assessment_date || '').toLowerCase();
      return patientName.includes(query) || leadName.includes(query) || date.includes(query);
    });
  }, [assessments, assessmentSearchQuery]);

  useEffect(() => {
    // If parent supplies a fixed patient (PatientDetail flow), keep in sync.
    if (patient) {
      setSelectedPatient(patient);
      if (!isEditing) setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id, isEditing]);

  useEffect(() => {
    if (!editingInvoiceId) {
      editPrefillAppliedRef.current = null;
      return;
    }
    if (!initialExportMetadata || !initialTemplateCode) return;
    if (editPrefillAppliedRef.current === editingInvoiceId) return;
    editPrefillAppliedRef.current = editingInvoiceId;

    setSelectedCode(initialTemplateCode);
    setStep(2);
    if (initialExportMetadata.period_from && initialExportMetadata.period_to) {
      setPeriod({ period_from: initialExportMetadata.period_from, period_to: initialExportMetadata.period_to });
    }
    if (initialExportMetadata.payment_mode) setPaymentMode(initialExportMetadata.payment_mode);
    if (initialExportMetadata.payment_date) {
      setPaymentDate(initialExportMetadata.payment_date);
    }
    if (initialExportMetadata.notes !== undefined) setNotes(initialExportMetadata.notes || '');
    if (initialExportMetadata.receipt_no) setReceiptNo(initialExportMetadata.receipt_no);
    if (initialExportMetadata.sheet_title) {
      setSheetTitle(initialExportMetadata.sheet_title);
    } else if (initialExportMetadata.data_shape && SHAPE_CONFIG[initialExportMetadata.data_shape]?.defaultSheetTitle) {
      setSheetTitle(SHAPE_CONFIG[initialExportMetadata.data_shape].defaultSheetTitle);
    }
    if (Array.isArray(initialExportMetadata.items)) {
      setItems(initialExportMetadata.items.map((it) => ({ manual_total: false, ...it })));
    }
    if (Array.isArray(initialExportMetadata.days)) {
      setDays(initialExportMetadata.days);
    }
  }, [editingInvoiceId, initialTemplateCode, initialExportMetadata]);

  useEffect(() => {
    if (patient) return;
    if (entityMode !== 'patient') return;
    let cancelled = false;
    setLoadingPatients(true);
    patientsApi
      .getAll({ limit: 1000 })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data?.patients || [];
        setPatients(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setPatients([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPatients(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patient, entityMode]);

  useEffect(() => {
    if (!showEntityChoice || entityMode !== 'assessment') return;
    let cancelled = false;
    setLoadingAssessments(true);
    assessmentsApi
      .getAll({ limit: 500 })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.data?.assessments || [];
        setAssessments(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAssessments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAssessments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showEntityChoice, entityMode]);

  useEffect(() => {
    let cancelled = false;
    setLoadingTemplates(true);
    docxTemplatesApi
      .getActive()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || [];
        if (Array.isArray(data) && data.length > 0) {
          // Drop any rows with unknown shape (defensive — only the 5 known shapes are supported here).
          let usable = data.filter((row) => SHAPE_CONFIG[row.data_shape]);
          if (allowedShapesSet) {
            usable = usable.filter((row) => allowedShapesSet.has(row.data_shape));
          }
          setTemplates(usable.length > 0 ? usable : fallbackOptions);
        } else {
          setTemplates(fallbackOptions);
        }
      })
      .catch(() => {
        if (!cancelled) setTemplates(fallbackOptions);
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplates(false);
      });
    return () => { cancelled = true; };
  }, [allowedShapesSet, fallbackOptions]);

  useEffect(() => {
    if (!tpl || isEditing) return;
    setPeriod(defaultPeriod(tpl.period));
    if (tpl.data_shape === 'attendance_bifurcation') {
      setSheetTitle((prev) => prev.trim() || tpl.defaultSheetTitle || '');
    }
  }, [tpl?.code, tpl?.period, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEditing) return;
    if (!tpl || !period.period_from || !period.period_to || !selectedPatient?.id) return;
    let cancelled = false;
    setLoadingPrefill(true);
    patientsApi
      .getDocxPrefill(selectedPatient.id, {
        template_code: tpl.code,
        period_from: period.period_from,
        period_to: period.period_to,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        setPrefill(data);
        setItems((data.items || []).map((it) => ({ manual_total: false, ...it })));
        setDays(data.days || []);
        setReceiptNo(data.next_receipt_no || '');
      })
      .catch(() => toast.error('Failed to load defaults'))
      .finally(() => !cancelled && setLoadingPrefill(false));
    return () => { cancelled = true; };
  }, [tpl?.code, period.period_from, period.period_to, selectedPatient?.id, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  const grandTotal = useMemo(() => {
    if (!tpl) return 0;
    if (tpl.data_shape === 'attendance_bifurcation') return 0;
    return items.reduce((sum, it) => {
      const fee = parseFloat(it.per_session_charge) || 0;
      const sessions = parseInt(it.no_of_sessions, 10) || 0;
      const rowTotal = it.total !== '' && it.total !== undefined && it.total !== null && !isNaN(parseFloat(it.total))
        ? parseFloat(it.total)
        : fee * sessions;
      return sum + rowTotal;
    }, 0);
  }, [items, tpl]);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, ...patch };
      const fee = parseFloat(next.per_session_charge) || 0;
      const sess = parseInt(next.no_of_sessions, 10) || 0;

      // Allow manual override of Total (INR). By default it's fee*sessions,
      // but once user edits total we preserve it across fee/session edits.
      const hasTotalPatch = Object.prototype.hasOwnProperty.call(patch || {}, 'total');
      if (hasTotalPatch) {
        const raw = patch.total;
        if (raw === '' || raw === null || raw === undefined) {
          next.manual_total = false;
          next.total = fee * sess;
        } else {
          next.manual_total = true;
          next.total = parseFloat(raw) || 0;
        }
      } else if (!next.manual_total) {
        next.total = fee * sess;
      }
      return next;
    }));
  };

  const addItem = () => {
    if (!tpl) return;
    const blank = { plan_name: '', per_session_charge: 0, no_of_sessions: 0, total: 0, manual_total: false };
    if (tpl.data_shape === 'ongc_weekly') blank.present_dates = [];
    if (tpl.data_shape === 'ongc_session') blank.month = '';
    if (tpl.data_shape === 'prl_bill') blank.month_range = '';
    setItems((prev) => [...prev, blank]);
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const updateDay = (idx, patch) => {
    setDays((prev) => prev.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };

  const handleExport = async (renderStyle = 'default', exportFormat = 'pdf') => {
    if (!tpl) return;
    if (!selectedPatient?.id) return;
    setExporting(true);
    try {
      const payload = {
        template_code: tpl.code,
        period_from: period.period_from,
        period_to: period.period_to,
        payment_mode: paymentMode,
        payment_date: paymentDate,
        notes,
        format: exportFormat === 'docx' ? 'docx' : 'pdf',
        render_style: renderStyle,
      };
      if (tpl.data_shape === 'attendance_bifurcation') {
        payload.days = days;
        payload.total_present = days.filter((d) => d.status === 'P' || /\(P\)/.test(d.status)).length;
        payload.sheet_title = sheetTitle.trim() || tpl.defaultSheetTitle || '';
      } else {
        payload.items = items.map((it) => ({
          plan_name: it.plan_name,
          per_session_charge: parseFloat(it.per_session_charge) || 0,
          no_of_sessions: parseInt(it.no_of_sessions, 10) || 0,
          present_dates: Array.isArray(it.present_dates) ? it.present_dates : [],
          month: it.month || '',
          month_range: it.month_range || '',
          total: it.total !== undefined && it.total !== null && it.total !== '' ? parseFloat(it.total) : undefined,
        }));
      }
      // If editing an existing template invoice, update its export metadata then re-export via invoice docx endpoint.
      if (editingInvoiceId) {
        await invoicesApi.updateTemplateExport(editingInvoiceId, payload);
        const response = await invoicesApi.getDocx(
          editingInvoiceId,
          exportFormat === 'docx' ? 'docx' : 'pdf',
          renderStyle
        );
        const serverMime = response.headers?.['content-type'] || 'application/pdf';
        const isPdf = /pdf/i.test(serverMime);
        const blob = new Blob([response.data], { type: serverMime });
        const ext = isPdf ? 'pdf' : 'docx';
        let filename = `Invoice_${editingInvoiceId}.${ext}`;
        const cd = response.headers?.['content-disposition'];
        if (cd) {
          const m = /filename="?([^"]+)"?/i.exec(cd);
          if (m && m[1]) filename = m[1];
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        if (onCreatedInvoiceId) onCreatedInvoiceId(String(editingInvoiceId));
        toast.success(isPdf ? 'PDF ready' : 'Export ready');
        onExported && onExported();
        onClose();
        return;
      }

      const res = await patientsApi.exportDocx(selectedPatient.id, payload);
      const createdInvoiceId = res?.headers?.['x-invoice-id'] || res?.headers?.['X-Invoice-Id'];
      if (createdInvoiceId && onCreatedInvoiceId) {
        onCreatedInvoiceId(createdInvoiceId);
      }
      const serverMime = res.headers?.['content-type'] || 'application/pdf';
      const isPdf = /pdf/i.test(serverMime);
      const blob = new Blob([res.data], { type: serverMime });
      if (blob.size === 0) {
        throw new Error('Export returned an empty file (0 KB).');
      }
      const ext = isPdf ? 'pdf' : 'docx';
      let filename = `${selectedPatient.full_name || 'patient'}_${tpl.code}_${receiptNo || 'export'}.${ext}`;
      const cd = res.headers?.['content-disposition'];
      if (cd) {
        const m = /filename="?([^"]+)"?/i.exec(cd);
        if (m && m[1]) filename = m[1];
      }
      const fallbackReason = res.headers?.['x-pdf-fallback-reason'];
      if (renderStyle === 'letterhead' && !isPdf) {
        toast(
          'Image template works only on PDF. This export downloaded as DOCX because PDF conversion failed on the server.',
          { icon: 'ℹ️', duration: 7000 }
        );
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (fallbackReason === 'libreoffice-not-installed') {
        toast('Downloaded as DOCX — install LibreOffice on the server to enable PDF export.', { icon: 'ℹ️', duration: 6000 });
      } else if (fallbackReason) {
        toast('Downloaded as DOCX — PDF conversion failed.', { icon: 'ℹ️', duration: 5000 });
      } else {
        toast.success(isPdf ? 'PDF ready' : 'Export ready');
      }
      onExported && onExported();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to export document');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="modal-shell flex items-center justify-center">
      <div className="modal-panel max-w-5xl">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {(step === 2 || (step === 1 && !patient)) && (
              <button
                onClick={() => setStep(step === 2 ? 1 : 0)}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiOutlineArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {step === 0
                  ? (entityMode === 'assessment' ? 'Assessment fees invoice' : 'Select Patient')
                  : step === 1
                    ? step1Title
                    : (selectedTemplate?.name || tpl?.label || 'Export')}
              </h3>
              <p className="text-sm text-gray-500">
                {step === 0
                  ? (entityMode === 'assessment'
                    ? 'Choose an assessment to bill (no patient record required).'
                    : 'Choose the patient to create this export for.')
                  : step === 1
                    ? `Pick a template to export for ${selectedPatient?.full_name || '—'}`
                    : `For ${selectedPatient?.full_name || '—'} (${selectedPatient?.patient_code || ''})`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          {step === 0 && (
            <div className="space-y-4">
              {showEntityChoice && (
                <div className="flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => {
                      setEntityMode('patient');
                      setSelectedAssessment(null);
                    }}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                      entityMode === 'patient'
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Patient
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEntityMode('assessment');
                      setSelectedPatient(null);
                    }}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                      entityMode === 'assessment'
                        ? 'bg-white text-primary-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Assessment fees
                  </button>
                </div>
              )}

              {entityMode === 'assessment' && showEntityChoice ? (
                loadingAssessments ? (
                  <div className="text-center text-gray-500 py-8">Loading assessments...</div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Search assessment</label>
                      <div className="relative">
                        <HiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                          type="search"
                          value={assessmentSearchQuery}
                          onChange={(e) => setAssessmentSearchQuery(e.target.value)}
                          placeholder="Lead name, patient name, or date"
                          className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                          autoComplete="off"
                        />
                      </div>
                      {assessmentSearchQuery.trim() ? (
                        <p className="mt-1.5 text-xs text-gray-500">
                          Showing {filteredAssessments.length} of {assessments.length} assessments
                        </p>
                      ) : null}
                    </div>
                    <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                      {filteredAssessments.length === 0 ? (
                        <p className="px-4 py-8 text-center text-sm text-gray-500">
                          No assessments match your search.
                        </p>
                      ) : (
                        filteredAssessments.map((a) => {
                          const isSelected = String(selectedAssessment?.id) === String(a.id);
                          return (
                            <button
                              key={a.id}
                              type="button"
                              onClick={() => setSelectedAssessment(a)}
                              className={`w-full px-4 py-3 text-left transition ${
                                isSelected
                                  ? 'bg-primary-50 border-l-4 border-primary-500'
                                  : 'hover:bg-gray-50 border-l-4 border-transparent'
                              }`}
                            >
                              <p className="font-medium text-gray-900">{formatAssessmentLabel(a)}</p>
                              {a.invoice ? (
                                <p className="text-xs text-amber-700">Has invoice: {a.invoice.invoice_number}</p>
                              ) : null}
                            </button>
                          );
                        })
                      )}
                    </div>
                    {selectedAssessment ? (
                      <p className="text-sm text-emerald-700">
                        Selected: {formatAssessmentLabel(selectedAssessment)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">
                        Choose an assessment, or continue to pick one in the invoice form.
                      </p>
                    )}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          if (onChooseAssessmentInvoice) {
                            onChooseAssessmentInvoice(selectedAssessment || null);
                          }
                        }}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
                      >
                        Create assessment invoice
                      </button>
                    </div>
                  </>
                )
              ) : loadingPatients ? (
                <div className="text-center text-gray-500 py-8">Loading patients...</div>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Search patient</label>
                    <div className="relative">
                      <HiOutlineSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        type="search"
                        value={patientSearchQuery}
                        onChange={(e) => setPatientSearchQuery(e.target.value)}
                        placeholder="Name, patient code, or phone number"
                        className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-transparent focus:ring-2 focus:ring-primary-500"
                        autoComplete="off"
                      />
                    </div>
                    {patientSearchQuery.trim() ? (
                      <p className="mt-1.5 text-xs text-gray-500">
                        Showing {filteredPatients.length} of {patients.length} patients
                      </p>
                    ) : null}
                  </div>
                  <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {filteredPatients.length === 0 ? (
                      <p className="px-4 py-8 text-center text-sm text-gray-500">
                        No patients match your search.
                      </p>
                    ) : (
                      filteredPatients.map((p) => {
                        const isSelected = String(selectedPatient?.id) === String(p.id);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setSelectedPatient(p)}
                            className={`w-full px-4 py-3 text-left transition ${
                              isSelected
                                ? 'bg-primary-50 border-l-4 border-primary-500'
                                : 'hover:bg-gray-50 border-l-4 border-transparent'
                            }`}
                          >
                            <p className="font-medium text-gray-900">{p.full_name}</p>
                            <p className="text-xs text-gray-500">
                              {[p.patient_code, p.contact_primary].filter(Boolean).join(' • ')}
                            </p>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {selectedPatient ? (
                    <p className="text-sm text-emerald-700">
                      Selected: {selectedPatient.full_name}
                      {selectedPatient.patient_code ? ` (${selectedPatient.patient_code})` : ''}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">Choose a patient from the list to continue.</p>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={!selectedPatient?.id}
                      onClick={() => setStep(1)}
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg disabled:opacity-50"
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 1 && (
            <>
              {loadingTemplates ? (
                <div className="text-center text-gray-500 py-8">Loading templates...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {includeDefaultInvoiceOption && (
                    <button
                      type="button"
                      onClick={() => {
                        if (onChooseDefaultInvoice) onChooseDefaultInvoice(selectedPatient);
                      }}
                      className="text-left border border-gray-200 hover:border-primary-500 hover:shadow-md rounded-lg p-4 transition"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
                          <HiOutlineDocumentDownload className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">Default invoice</div>
                          <div className="text-xs text-gray-500">Use the standard invoice form</div>
                        </div>
                      </div>
                    </button>
                  )}
                  {templates.map((opt) => {
                    const cfg = SHAPE_CONFIG[opt.data_shape];
                    const blurb = opt.description || cfg?.blurb || '';
                    return (
                      <button
                        key={opt.template_code}
                        onClick={() => { setSelectedCode(opt.template_code); setStep(2); }}
                        className="text-left border border-gray-200 hover:border-primary-500 hover:shadow-md rounded-lg p-4 transition"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                            <HiOutlineDocumentDownload className="w-6 h-6" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{opt.name || cfg?.label || opt.template_code}</div>
                            <div className="text-xs text-gray-500 font-mono">{opt.template_code}</div>
                          </div>
                          {(opt.is_system === true || opt.is_system === 1) && (
                            <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                              Default
                            </span>
                          )}
                        </div>
                        {blurb && <p className="mt-2 text-sm text-gray-500">{blurb}</p>}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {step === 2 && !tpl && (
            <div className="flex items-center justify-center py-16">
              {loadingTemplates ? (
                <div className="text-center text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent mx-auto mb-3" />
                  Loading invoice…
                </div>
              ) : (
                <div className="text-center text-gray-500 max-w-md">
                  <p className="font-medium text-gray-700">Template not available</p>
                  <p className="text-sm mt-1">
                    The template code for this invoice is no longer active. Contact an admin to restore template{' '}
                    <span className="font-mono">{selectedCode || initialTemplateCode || '—'}</span>.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 2 && tpl && (
            <div className="space-y-6">
              {/* Period + payment */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <PeriodPickers
                  tpl={tpl}
                  period={period}
                  setPeriod={setPeriod}
                  manualWeekRange={manualWeekRange}
                  setManualWeekRange={setManualWeekRange}
                />
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Payment Mode</label>
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Receipt / Invoice Date</label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              {/* Sessions / attendance source hint */}
              {!loadingPrefill && tpl.data_shape !== 'attendance_bifurcation' && prefill && (
                <div className={`text-xs rounded-lg px-3 py-2 ${
                  prefill.present_count === 0
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : prefill.present_source === 'appointments'
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                }`}>
                  {prefill.present_count === 0 ? (
                    <>No present attendance found for this period. <strong>Set "No of Sessions" manually below.</strong></>
                  ) : prefill.present_source === 'appointments' ? (
                    <>Found <strong>{prefill.present_count}</strong> completed appointment day{prefill.present_count === 1 ? '' : 's'} in this period (used as Sessions).</>
                  ) : (
                    <>Found <strong>{prefill.present_count}</strong> present day{prefill.present_count === 1 ? '' : 's'} from marked attendance.</>
                  )}
                </div>
              )}

              {tpl.data_shape === 'attendance_bifurcation' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Report heading</label>
                  <input
                    type="text"
                    value={sheetTitle}
                    onChange={(e) => setSheetTitle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder={tpl.defaultSheetTitle || 'Attendance sheet title'}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Edit the title shown at the top of the report. Use Word (DOCX) export to change it further after download.
                  </p>
                </div>
              )}

              {/* Items / days editor */}
              {tpl.data_shape === 'attendance_bifurcation' ? (
                <DaysEditor
                  days={days}
                  updateDay={updateDay}
                  loading={loadingPrefill}
                  patientName={patient?.full_name || ''}
                />
              ) : (
                <ItemsEditor
                  tpl={tpl}
                  items={items}
                  prefill={prefill}
                  updateItem={updateItem}
                  removeItem={removeItem}
                  addItem={addItem}
                  loading={loadingPrefill}
                />
              )}

              {/* Notes + grand total */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                {tpl.data_shape !== 'attendance_bifurcation' && (
                  <div className="bg-primary-50 rounded-lg p-3 flex flex-col justify-center">
                    <div className="text-xs text-primary-700">Grand Total</div>
                    <div className="text-2xl font-bold text-primary-700">Rs. {grandTotal.toLocaleString('en-IN')}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                disabled={exporting}
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
              >
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          {step === 2 && (
            <button
              disabled={exporting}
              onClick={() => setShowTemplateStyle(true)}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-60"
            >
              <HiOutlineDocumentDownload className="w-5 h-5" />
              {exporting ? 'Exporting...' : (tpl?.data_shape === 'attendance_bifurcation' ? 'Export report' : 'Export PDF')}
            </button>
          )}
          </div>
        </div>
      </div>

      {showTemplateStyle && (
        <div className="modal-shell flex items-center justify-center">
          <div className="modal-panel max-w-md">
            <div className="p-4 sm:p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Choose template style</h3>
              <p className="mt-1 text-sm text-gray-500">
                Default uses the existing export. Image template adds the clinic header & footer.
              </p>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              {tpl?.data_shape === 'attendance_bifurcation' && (
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => {
                    setShowTemplateStyle(false);
                    handleExport('default', 'docx');
                  }}
                  className="w-full px-4 py-2.5 border border-primary-300 bg-primary-50 text-primary-800 rounded-lg hover:bg-primary-100 font-medium disabled:opacity-60"
                >
                  Word (.docx) — editable in Microsoft Word
                </button>
              )}
              <button
                type="button"
                disabled={exporting}
                onClick={() => {
                  setShowTemplateStyle(false);
                  handleExport('default', 'pdf');
                }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-60"
              >
                {tpl?.data_shape === 'attendance_bifurcation' ? 'PDF — default template' : 'Default template'}
              </button>
              <button
                type="button"
                disabled={exporting}
                onClick={() => {
                  setShowTemplateStyle(false);
                  handleExport('letterhead', 'pdf');
                }}
                className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-60"
              >
                {tpl?.data_shape === 'attendance_bifurcation' ? 'PDF — image template (header + footer)' : 'Image template (Header + Footer)'}
              </button>
            </div>
            <div className="p-4 sm:p-5 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                disabled={exporting}
                onClick={() => setShowTemplateStyle(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PeriodPickers({ tpl, period, setPeriod, manualWeekRange, setManualWeekRange }) {
  if (tpl.period === 'week') {
    return (
      <>
        <div className="md:col-span-4">
          <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
            <input
              type="checkbox"
              checked={!!manualWeekRange}
              onChange={(e) => {
                const next = !!e.target.checked;
                setManualWeekRange(next);
                if (!next && period.period_from) {
                  const d = new Date(period.period_from);
                  if (!isNaN(d.getTime())) {
                    const s = startOfWeek(d);
                    setPeriod({ period_from: ymd(s), period_to: ymd(endOfWeek(s)) });
                  }
                }
              }}
              className="h-4 w-4 rounded border-gray-300 text-primary-600"
            />
            Select dates manually (not fixed Monday–Sunday)
          </label>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {manualWeekRange ? 'Start date' : 'Week Start (Monday)'}
          </label>
          <input
            type="date"
            value={period.period_from}
            onChange={(e) => {
              const val = e.target.value;
              if (manualWeekRange) {
                setPeriod((p) => ({ ...p, period_from: val }));
                return;
              }
              const d = new Date(val);
              if (isNaN(d.getTime())) return;
              const s = startOfWeek(d);
              setPeriod({ period_from: ymd(s), period_to: ymd(endOfWeek(s)) });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            {manualWeekRange ? 'End date' : 'Week End (Sunday)'}
          </label>
          <input
            type="date"
            value={period.period_to}
            disabled={!manualWeekRange}
            onChange={(e) => setPeriod((p) => ({ ...p, period_to: e.target.value }))}
            className={
              manualWeekRange
                ? 'w-full px-3 py-2 border border-gray-300 rounded-lg'
                : 'w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500'
            }
          />
        </div>
      </>
    );
  }
  if (tpl.period === 'range') {
    return (
      <>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input
            type="date"
            value={period.period_from}
            onChange={(e) => setPeriod((p) => ({ ...p, period_from: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input
            type="date"
            value={period.period_to}
            onChange={(e) => setPeriod((p) => ({ ...p, period_to: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </>
    );
  }
  return (
    <div className="md:col-span-2">
      <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
      <input
        type="month"
        value={period.period_from ? period.period_from.slice(0, 7) : ''}
        onChange={(e) => {
          const [y, m] = e.target.value.split('-').map(Number);
          if (!y || !m) return;
          const s = new Date(y, m - 1, 1);
          const en = new Date(y, m, 0);
          setPeriod({ period_from: ymd(s), period_to: ymd(en) });
        }}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />
    </div>
  );
}

function ItemsEditor({ tpl, items, updateItem, removeItem, addItem, loading }) {
  const cols = tpl.columns;
  const isOngcWeekly = tpl.data_shape === 'ongc_weekly';
  const rowSpan = Math.max(1, items.length || 1);

  const setAll = (patch) => {
    items.forEach((_, i) => updateItem(i, patch));
  };

  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <span className="font-semibold text-sm text-gray-700">Items</span>
        <button
          onClick={addItem}
          className="text-primary-600 hover:bg-primary-50 px-2 py-1 rounded flex items-center gap-1 text-sm"
        >
          <HiOutlinePlus className="w-4 h-4" /> Add row
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              {cols.includes('plan_name') && <th className="text-left px-3 py-2">Treatment Plan</th>}
              {cols.includes('present_dates') && <th className="text-left px-3 py-2">Present Dates (MM/DD/YYYY)</th>}
              {cols.includes('month') && <th className="text-left px-3 py-2">Month</th>}
              {cols.includes('month_range') && <th className="text-left px-3 py-2">Month Range</th>}
              {cols.includes('per_session_charge') && <th className="text-right px-3 py-2">Per Session (INR)</th>}
              {cols.includes('no_of_sessions') && <th className="text-right px-3 py-2">No of Sessions</th>}
              {cols.includes('total') && <th className="text-right px-3 py-2">Total (INR)</th>}
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={cols.length + 1} className="px-3 py-4 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={cols.length + 1} className="px-3 py-4 text-center text-gray-400">No items. Click "Add row".</td></tr>
            )}
            {!loading && items.map((it, idx) => (
              <tr key={idx} className="border-t border-gray-100">
                {cols.includes('plan_name') && (
                  <td className="px-3 py-2">
                    <input
                      value={it.plan_name || ''}
                      onChange={(e) => updateItem(idx, { plan_name: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded"
                      placeholder="e.g. Occupational Therapy"
                    />
                  </td>
                )}
                {cols.includes('present_dates') && (
                  isOngcWeekly ? (
                    idx === 0 ? (
                      <td className="px-3 py-2 align-middle" rowSpan={rowSpan}>
                        <textarea
                          value={(items[0]?.present_dates || []).join('\n')}
                          onChange={(e) => {
                            const arr = e.target.value.split(/\s*\n\s*/).filter(Boolean);
                            setAll({ present_dates: arr });
                          }}
                          rows={Math.min(10, Math.max(3, rowSpan * 3))}
                          className="w-full px-2 py-1 border border-gray-200 rounded font-mono text-xs text-center"
                          placeholder="01/13/2025"
                        />
                      </td>
                    ) : null
                  ) : (
                    <td className="px-3 py-2 align-top">
                      <textarea
                        value={(it.present_dates || []).join('\n')}
                        onChange={(e) => updateItem(idx, { present_dates: e.target.value.split(/\s*\n\s*/).filter(Boolean) })}
                        rows={3}
                        className="w-full px-2 py-1 border border-gray-200 rounded font-mono text-xs"
                        placeholder="01/13/2025"
                      />
                    </td>
                  )
                )}
                {cols.includes('month') && (
                  <td className="px-3 py-2">
                    <input
                      value={it.month || ''}
                      onChange={(e) => updateItem(idx, { month: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded"
                      placeholder="e.g. February 2025"
                    />
                  </td>
                )}
                {cols.includes('month_range') && (
                  <td className="px-3 py-2">
                    <input
                      value={it.month_range || ''}
                      onChange={(e) => updateItem(idx, { month_range: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-200 rounded"
                      placeholder="e.g. 11th August 2025 To 21st September 2025"
                    />
                  </td>
                )}
                {cols.includes('per_session_charge') && (
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      value={it.per_session_charge ?? 0}
                      onChange={(e) => updateItem(idx, { per_session_charge: e.target.value })}
                      className="w-full sm:w-24 px-2 py-1 border border-gray-200 rounded text-right"
                    />
                  </td>
                )}
                {cols.includes('no_of_sessions') && (
                  isOngcWeekly ? (
                    idx === 0 ? (
                      <td className="px-3 py-2 align-middle" rowSpan={rowSpan}>
                        <input
                          type="number"
                          min="0"
                          value={items[0]?.no_of_sessions ?? 0}
                          onChange={(e) => setAll({ no_of_sessions: e.target.value })}
                          className="w-full sm:w-24 px-2 py-1 border border-gray-200 rounded text-center"
                        />
                      </td>
                    ) : null
                  ) : (
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        value={it.no_of_sessions ?? 0}
                        onChange={(e) => updateItem(idx, { no_of_sessions: e.target.value })}
                        className="w-full sm:w-20 px-2 py-1 border border-gray-200 rounded text-right"
                      />
                    </td>
                  )
                )}
                {cols.includes('total') && (
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min="0"
                      value={it.total ?? 0}
                      onChange={(e) => updateItem(idx, { total: e.target.value })}
                      className="w-full sm:w-28 px-2 py-1 border border-gray-200 rounded text-right"
                    />
                  </td>
                )}
                <td className="px-2 py-2">
                  <button
                    onClick={() => removeItem(idx)}
                    className="text-red-500 hover:bg-red-50 p-1 rounded"
                    title="Remove row"
                  >
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DaysEditor({ days, updateDay, loading, patientName }) {
  const rowSpan = Math.max(1, days.length || 1);
  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
        <span className="font-semibold text-sm text-gray-700">Daily Attendance</span>
        <span className="text-xs text-gray-500">P = Present, A = Absent, edit any cell</span>
      </div>
      <div className="overflow-x-auto max-h-96">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && days.length === 0 && (
              <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Pick a month to load days.</td></tr>
            )}
            {!loading && days.map((d, idx) => (
              <tr key={idx} className="border-t border-gray-100">
                {idx === 0 ? (
                  <td className="px-3 py-1 align-middle text-center font-medium text-gray-800" rowSpan={rowSpan}>
                    {patientName || '—'}
                  </td>
                ) : null}
                <td className="px-3 py-1 font-mono text-xs">{d.date}</td>
                <td className="px-3 py-1">
                  <input
                    value={d.status || ''}
                    onChange={(e) => updateDay(idx, { status: e.target.value })}
                    className="w-full px-2 py-1 border border-gray-200 rounded"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
